import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertOctagon, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Skeleton } from '@/components/ui/Skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Fact, QuietEmpty, StoredImage, WorkerChip } from './shared';
import { useReviewDefect, useWorkDefects } from '@/features/workMode/hooks';
import { describeWorkError } from '@/features/workMode/api';
import { DEFECT_STATUS_LABEL, DEFECT_STATUS_VARIANT, DISPOSITION_LABEL, SEVERITY_LABEL, SEVERITY_VARIANT } from '@/features/workMode/labels';
import type { WorkLookups } from '@/features/workMode/useLookups';
import type { WorkDefect, WorkDefectStatus } from '@/features/workMode/types';
import { scrapTagLabel } from '@/features/scrap/tags';
import { formatDuration } from '@/features/workMode/api';
import { formatDateTime, formatRelative } from '@/lib/formatters';

interface DefectsTabProps {
  lookups: WorkLookups;
  canManage: boolean;
}

type Filter = WorkDefectStatus | 'all';
const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'open', label: 'Open' },
  { id: 'reviewed', label: 'Reviewed' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'all', label: 'All' },
];

/** Everything the benches flagged: photos first, then what was lost and where in the build it happened. */
export function DefectsTab({ lookups, canManage }: DefectsTabProps) {
  const [filter, setFilter] = useState<Filter>('open');
  const { data: defects = [], isLoading } = useWorkDefects(filter);
  const [selected, setSelected] = useState<WorkDefect | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const totals = useMemo(() => {
    let parts = 0;
    let critical = 0;
    for (const d of defects) {
      critical += d.severity === 'critical' ? 1 : 0;
      for (const p of d.scrapped_parts ?? []) parts += Number(p.qty) || 0;
    }
    return { parts, critical };
  }, [defects]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                filter === f.id ? 'border-info bg-info-soft/30 text-info' : 'border-border text-muted-foreground hover:bg-secondary',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">
          {defects.length} report{defects.length === 1 ? '' : 's'}
          {totals.critical ? <span className="text-fresh-salmon-dark"> · {totals.critical} critical</span> : null}
          {totals.parts ? ` · ${totals.parts} part${totals.parts === 1 ? '' : 's'} scrapped` : ''}
          {' · '}
          <Link to="/mfg/scrap-log" className="text-foreground underline-offset-2 hover:underline">
            Scrap log
          </Link>
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      ) : defects.length === 0 ? (
        <QuietEmpty>{filter === 'open' ? 'No open defect reports. Nice.' : 'Nothing here yet.'}</QuietEmpty>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {defects.map((d) => (
            <li key={d.id}>
              <DefectCard defect={d} lookups={lookups} onOpen={() => setSelected(d)} onPhoto={setLightbox} />
            </li>
          ))}
        </ul>
      )}

      <DefectSheet defect={selected} lookups={lookups} canManage={canManage} onOpenChange={(o) => !o && setSelected(null)} onPhoto={setLightbox} />

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {lightbox && <StoredImage bucket="work-captures" path={lightbox} alt="Defect photo" className="max-h-[80vh] w-full object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DefectCard({ defect: d, lookups, onOpen, onPhoto }: { defect: WorkDefect; lookups: WorkLookups; onOpen: () => void; onPhoto: (path: string) => void }) {
  const photos = d.photo_paths ?? [];
  const scrapBits = [
    (d.scrap_tags ?? []).length > 0 ? (d.scrap_tags ?? []).map(scrapTagLabel).join(', ') : null,
    (d.scrapped_parts ?? []).length > 0 ? (d.scrapped_parts ?? []).map((p) => `${p.qty}× ${p.name}`).join(', ') : null,
    d.committed_seconds != null ? `${formatDuration(d.committed_seconds)} on the part` : null,
  ].filter(Boolean);
  return (
    <article className="flex gap-3 rounded-2xl border border-border bg-card p-3">
      <div className="flex w-24 shrink-0 flex-col gap-1.5">
        {photos.length === 0 ? (
          <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
            <Camera className="h-5 w-5" />
          </div>
        ) : (
          <>
            <StoredImage bucket="work-captures" path={photos[0]} alt="Defect" className="h-24 w-24" onClick={() => onPhoto(photos[0])} />
            {photos.length > 1 && <span className="text-center text-[11px] text-muted-foreground">+{photos.length - 1} more</span>}
          </>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant={SEVERITY_VARIANT[d.severity]}>{SEVERITY_LABEL[d.severity]}</Badge>
          <Badge variant="outline">{DISPOSITION_LABEL[d.disposition]}</Badge>
          <Badge variant={DEFECT_STATUS_VARIANT[d.status]}>{DEFECT_STATUS_LABEL[d.status]}</Badge>
          {d.needs_rework && <Badge variant="warning-soft">Rework</Badge>}
        </div>
        <button type="button" onClick={onOpen} className="mt-1.5 block w-full text-left">
          <p className="line-clamp-2 text-sm text-foreground">{d.description}</p>
        </button>
        <p className="mt-1 truncate text-xs text-muted-foreground">
          {lookups.personName(d.user_id)} · {lookups.stationName(d.workstation_id) ?? 'No station'}
          {d.item_id ? ` · ${lookups.itemName(d.item_id)}` : ''}
          {d.step_number ? ` · step ${d.step_number}` : d.progress_pct != null ? ` · ${d.progress_pct}% through` : ''}
        </p>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <p className="truncate text-xs text-muted-foreground">{scrapBits.join(' · ') || 'No parts scrapped'}</p>
          <span className="shrink-0 text-xs text-muted-foreground">{formatRelative(d.created_at)}</span>
        </div>
      </div>
    </article>
  );
}

function DefectSheet({
  defect: d,
  lookups,
  canManage,
  onOpenChange,
  onPhoto,
}: {
  defect: WorkDefect | null;
  lookups: WorkLookups;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  onPhoto: (path: string) => void;
}) {
  return (
    <Sheet open={!!d} onOpenChange={onOpenChange}>
      <SheetContent>{d && <DefectDetail key={d.id} defect={d} lookups={lookups} canManage={canManage} onOpenChange={onOpenChange} onPhoto={onPhoto} />}</SheetContent>
    </Sheet>
  );
}

function DefectDetail({
  defect: d,
  lookups,
  canManage,
  onOpenChange,
  onPhoto,
}: {
  defect: WorkDefect;
  lookups: WorkLookups;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  onPhoto: (path: string) => void;
}) {
  const review = useReviewDefect();
  const [status, setStatus] = useState<WorkDefectStatus>(d.status === 'open' ? 'reviewed' : d.status);
  const [notes, setNotes] = useState(d.review_notes ?? '');

  const save = () => {
    review.mutate(
      { id: d.id, status, reviewNotes: notes.trim() || null },
      {
        onSuccess: () => {
          toast.success('Review saved.');
          onOpenChange(false);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-fresh-salmon-dark" />
            Defect report
          </SheetTitle>
          <SheetDescription>
            {formatDateTime(d.created_at)} · {lookups.stationName(d.workstation_id) ?? 'No station'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-5">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={SEVERITY_VARIANT[d.severity]}>{SEVERITY_LABEL[d.severity]}</Badge>
            <Badge variant="outline">{DISPOSITION_LABEL[d.disposition]}</Badge>
            <Badge variant={DEFECT_STATUS_VARIANT[d.status]}>{DEFECT_STATUS_LABEL[d.status]}</Badge>
          </div>

          <WorkerChip person={lookups.person(d.user_id)} sub={d.item_id ? `Building ${lookups.itemName(d.item_id)}` : undefined} size={40} />

          <p className="whitespace-pre-line text-sm text-foreground">{d.description}</p>

          {(d.photo_paths ?? []).length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {d.photo_paths.map((p) => (
                <StoredImage key={p} bucket="work-captures" path={p} alt="Defect photo" className="aspect-square w-full" onClick={() => onPhoto(p)} />
              ))}
            </div>
          )}

          {(d.scrap_tags ?? []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {d.scrap_tags.map((t) => (
                <Badge key={t} variant="outline">
                  {scrapTagLabel(t)}
                </Badge>
              ))}
              {d.needs_rework && <Badge variant="warning-soft">Needs rework</Badge>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Fact label="How far along">{d.step_number ? `Step ${d.step_number}` : d.progress_pct != null ? `${d.progress_pct}% through the build` : 'Not said'}</Fact>
            <Fact label="Reported from">{lookups.stationName(d.workstation_id) ?? '—'}</Fact>
            {d.committed_seconds != null && <Fact label="Time on the part">{formatDuration(d.committed_seconds)}</Fact>}
            {d.needs_rework && d.rework_notes ? (
              <Fact label="Rework">
                <span className="whitespace-pre-line">{d.rework_notes}</span>
              </Fact>
            ) : null}
          </div>

          <div>
            <p className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Parts scrapped</p>
            {(d.scrapped_parts ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">None recorded.</p>
            ) : (
              <ul className="divide-y divide-border rounded-xl border border-border">
                {d.scrapped_parts.map((p, i) => (
                  <li key={`${p.sku}-${i}`} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="min-w-0 truncate text-foreground">
                      {p.name} {p.sku && <span className="text-muted-foreground">{p.sku}</span>}
                    </span>
                    <span className="shrink-0 tabular-nums text-foreground">× {p.qty}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {d.reviewed_at && (
            <p className="text-xs text-muted-foreground">
              Reviewed {formatRelative(d.reviewed_at)} by {lookups.personName(d.reviewed_by)}.
            </p>
          )}

          {canManage && (
            <div className="space-y-3 rounded-xl border border-border p-4">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as WorkDefectStatus)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(DEFECT_STATUS_LABEL) as WorkDefectStatus[]).map((s) => (
                      <SelectItem key={s} value={s}>
                        {DEFECT_STATUS_LABEL[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="defect-notes">Review notes</Label>
                <Textarea id="defect-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Root cause, what changes, who follows up…" />
              </div>
            </div>
          )}
        </SheetBody>
        {canManage && (
          <SheetFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button onClick={save} disabled={review.isPending}>
              Save review
            </Button>
          </SheetFooter>
        )}
    </>
  );
}
